import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, Subject, map } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Profile } from '../models/profile';
import { IGenericJSON } from '../helpers/interface';

export interface ProfileCheckResponse {
  exists: boolean;
  profile?: Profile;
}

/** Read-only view of another user's profile, already redacted by the server. */
export interface PublicProfile {
  userId: number;
  userType: 'student' | 'faculty' | 'district' | string;
  preferredName: string | null;
  lastName: string;
  displayName: string;
  bio: string | null;
  profilePicturePath: string | null;
  careerId: number | null;
  interests: { id: number; name: string }[];
  /** True when the viewer is a student and private fields were withheld. */
  restricted: boolean;
  /** Only present for non-student viewers. */
  firstName?: string | null;
  email?: string | null;
  gradeLevel?: number | null;
  schoolName?: string | null;
}

@Injectable({ providedIn: 'root' })
export class ProfileService {
  private base = '/api/profiles';

  /** Emits whenever the authenticated user's profile is updated. */
  profileUpdated$ = new Subject<Profile>();

  constructor(private http: HttpClient) {}

  notifyUpdated(profile: Profile): void {
    this.profileUpdated$.next(profile);
  }

  /** Returns the authenticated user's profile, or `{ exists: false }` on 404. */
  getMyProfile(): Observable<ProfileCheckResponse> {
    return this.http.get<{ exists: boolean; profile?: IGenericJSON }>(`${this.base}/me`).pipe(
      map((response) => ({
        exists: response.exists,
        profile: response.profile ? Profile.digest(response.profile) : undefined,
      })),
      catchError((err) => {
        if (err.status === 404) return of({ exists: false });
        throw err;
      }),
    );
  }

  /**
   * Returns another user's read-only profile, or null when the user does not
   * exist. The server redacts fields based on the viewer: student viewers never
   * receive `firstName`, `email`, `gradeLevel`, or `schoolName`.
   */
  getPublicProfile(userId: number): Observable<PublicProfile | null> {
    return this.http
      .get<{ exists: boolean; profile?: PublicProfile }>(`${this.base}/users/${userId}`)
      .pipe(
        map((response) => (response.exists && response.profile ? response.profile : null)),
        catchError((err) => {
          if (err.status === 404) return of(null);
          throw err;
        }),
      );
  }

  /** Creates a profile owned by the authenticated user. */
  createMyProfile(data: Partial<Profile>): Observable<Profile> {
    return this.http
      .post<IGenericJSON>(`${this.base}/me`, data)
      .pipe(map((profile) => Profile.digest(profile)));
  }

  /** Updates the authenticated user's profile. */
  updateMyProfile(data: Partial<Profile>): Observable<Profile> {
    return this.http
      .put<IGenericJSON>(`${this.base}/me`, data)
      .pipe(map((profile) => Profile.digest(profile)));
  }

  /** Deletes the authenticated user's profile. */
  deleteMyProfile(): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.base}/me`);
  }
}
